import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchPurchaseCart,
  resetCheckoutResponse,
  updatePurchaseCartAsync,
} from '../store/purchaseSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AnonymousCheckoutResponse, PurchaseItem } from '../interfaces';
import { RootState } from '../store/store';
import { AppDispatch } from '../store/store';
import purchaseService from '../services/purchaseService';
import { toast } from 'react-toastify';
import { openFrame } from '../utils/iframeModal';
import { useOrderConfirmationSSE } from '../hooks/useOrderConfirmationSSE';
import backIcon from '/asset/back-arrow.svg';

const formatWithDecimals = (amount: number): string =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const splitHeroAmount = (amount: number) => {
  const formatted = formatWithDecimals(amount);
  const dot = formatted.lastIndexOf('.');
  return { main: formatted.slice(0, dot), decimal: formatted.slice(dot) };
};

const statusStyle = (status: string): string => {
  const s = String(status).toLowerCase();
  if (s === 'paid' || s === 'paidwithbnpl') return 'text-green-600';
  if (s === 'pending') return 'text-amber-500';
  return 'text-red-500';
};

const CopyIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    className="w-3.5 h-3.5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

interface CartResponseCardProps {
  cartResponse: AnonymousCheckoutResponse;
  setListeningResponse: (response: {
    orderStatus: string;
    paymentOption: string | number;
  }) => void;
  endConnection: () => void;
  bnplUrl?: string;
  bnplText?: string;
  paymentMode?: 0 | 1; // 0 for regular payment, 1 for BNPL
}

const CartResponseCard: React.FC<CartResponseCardProps> = ({
  cartResponse,
  setListeningResponse,
  endConnection,
  bnplUrl = '',
  bnplText = 'Pay Small Small',
  paymentMode = 0,
}) => {
  const [cart, setCart] = useState<PurchaseItem[]>([]);
  const [showDeliveryFee, setShowDeliveryFee] = useState(false);
  const [showVat, setShowVat] = useState(false);
  const [showProcessingFee, setShowProcessingFee] = useState(false);
  const [showRoundingDifference, setShowRoundingDifference] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string>('Pending');
  const dispatch: AppDispatch = useDispatch();
  const { purchaseCopy: purchaseData, miscellanousFeeName } = useSelector(
    (state: RootState) => state.purchase
  );
  const { cartLineItemsCopy, loading, orderDetailsCopy } = useSelector(
    (state: RootState) => state.purchase
  );
  const storeInfo = useSelector((state: RootState) => state.products.storeInfo);
  // const user = useSelector((state: RootState) => state.auth.user);

  const handleOrderConfirmed = useCallback(() => {
    setListeningResponse({
      orderStatus: 'Success',
      paymentOption: paymentMode,
    });
    setOrderStatus('Success');
    toast.success('Payment successful');
  }, [setListeningResponse, paymentMode]);

  // Live order confirmation: flip to Success automatically when payment lands.
  useOrderConfirmationSSE({
    purchaseCode: cartResponse?.purchaseCode,
    enabled: paymentMode === 0 && orderStatus !== 'Success',
    onConfirmed: handleOrderConfirmed,
  });

  // const bnplLink = import.meta.env.VITE_BNPL_URL as string
  // const companyCode = localStorage.getItem("bnplCompanyShortCode")

  useEffect(() => {
    setShowDeliveryFee(cartResponse?.deliveryFee > 0);
    setShowVat(cartResponse?.vat > 0);
    setShowProcessingFee(cartResponse?.processingFee > 0);
    setShowRoundingDifference(cartResponse?.roundingDifference !== 0);
  }, [
    cartResponse?.deliveryFee,
    cartResponse?.vat,
    cartResponse?.roundingDifference,
    cartResponse?.miscellaneousFee,
    cartResponse?.processingFee,
  ]);

  useEffect(() => {
    if (purchaseData) {
      if (purchaseData.purchases) {
        setCart(purchaseData.purchases);
      }
    }
  }, [purchaseData]);

  const heroAmount = splitHeroAmount(cartResponse?.totalAmountWithVAT ?? 0);

  const handleCopy = async (value: string, label: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div>
      {/* Back button */}
      <div
        className="h-auto w-[110px] text-[10px]
        font-dmsans bg-primary-100 text-primary-900
        mb-4 text-center py-[5px] hover:bg-primary-300 cursor-pointer
        flex items-center justify-center gap-1 rounded-md"
        onClick={() => {
          let CartLineItems: PurchaseItem[] = [];
          if (CartLineItems) CartLineItems = cartLineItemsCopy;
          dispatch(
            updatePurchaseCartAsync({
              CartLineItems,
              purchaseCode: '',
              silent: true,
            })
          ).then(() => {
            dispatch(fetchPurchaseCart()).then(() => {
              dispatch(resetCheckoutResponse());
              dispatch(fetchPurchaseCart());
            });
          });
          endConnection();
        }}
      >
        <img src={backIcon} alt="Back" className="w-[10px] h-[10px]" />
        Go back to cart
      </div>

      {/* Invoice card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        {/* Company header */}
        <div className="flex items-center gap-3 mb-5">
          {storeInfo?.coverImage ? (
            <img
              src={storeInfo.coverImage}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              alt="logo"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-500 text-xl leading-none">✦</span>
            </div>
          )}
          <span className="font-bold text-[18px] text-gray-900 font-dmsans">
            {storeInfo?.name ?? ''}
          </span>
        </div>

        {/* Hero amount */}
        <div className="mb-5">
          <span className="text-[40px] font-bold text-gray-900 font-dmsans leading-tight">
            {heroAmount.main}
          </span>
          <span className="text-[28px] font-normal text-gray-400 font-dmsans leading-tight">
            {heroAmount.decimal}
          </span>
        </div>

        <hr className="border-gray-100 mb-4" />

        {/* Invoice meta */}
        <div className="flex items-start divide-x divide-gray-200 mb-4 gap-0">
          <div className="pr-4">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Invoice No
            </p>
            <p className="text-[13px] font-medium text-gray-900 font-dmsans">
              {cartResponse?.orderId ?? '—'}
            </p>
          </div>
          <div className="px-4 flex-1 min-w-0">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Payment Status
            </p>
            <p
              className={`text-[13px] font-medium font-dmsans ${statusStyle(orderStatus)}`}
            >
              {orderStatus}
            </p>
          </div>
          <div className="px-4 flex-1 min-w-0">
            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
              Order Note
            </p>
            <p
              className={`text-[13px] font-medium font-dmsans
                text-gray-900`}
            >
              {orderDetailsCopy?.deliveryInstructions}
            </p>
          </div>
        </div>

        <hr className="border-gray-100 mb-5" />

        {/* Items table */}
        <div>
          <h3 className="font-semibold text-[15px] text-gray-900 font-dmsans mb-3">
            Ordered Item(s)
          </h3>
          <table className="w-full font-dmsans">
            <thead>
              <tr>
                <th className="text-left text-[13px] font-normal text-gray-500 pb-2.5">
                  Line Item
                </th>
                <th className="text-center text-[13px] font-normal text-gray-500 pb-2.5">
                  Quantity
                </th>
                <th className="text-right text-[13px] font-normal text-gray-500 pb-2.5">
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {cart?.map((item, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-2.5 text-[13px] text-gray-900">
                    {item.name}
                    {item.sku && (
                      <div className="text-[11px] text-gray-500">
                        SKU: {item.sku}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 text-[13px] text-gray-900 text-center">
                    {item.quantity}
                  </td>
                  <td className="py-2.5 text-[13px] text-gray-900 text-right">
                    {formatWithDecimals(
                      (item.unitPrice ?? 0) * (item.quantity ?? 1)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary box */}
        <div className="mt-4 bg-gray-50 rounded-xl p-4 font-dmsans">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-[13px] text-gray-500">Subtotal</span>
              <span className="text-[13px] text-gray-900">
                {formatWithDecimals(cartResponse?.totalAmount ?? 0)}
              </span>
            </div>
            {showVat && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">VAT</span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.vat ?? 0)}
                </span>
              </div>
            )}
            {cartResponse?.consumptionFee > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  Consumption Fee
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.consumptionFee ?? 0)}
                </span>
              </div>
            )}
            {cartResponse?.miscellaneousFee > 0 && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  {miscellanousFeeName || 'Miscellanous Fee'}
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.miscellaneousFee ?? 0)}
                </span>
              </div>
            )}
            {showRoundingDifference && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  Rounding Difference
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.roundingDifference ?? 0)}
                </span>
              </div>
            )}
            {showDeliveryFee && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">Delivery</span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.deliveryFee ?? 0)}
                </span>
              </div>
            )}
            {showProcessingFee && (
              <div className="flex justify-between">
                <span className="text-[13px] text-gray-500">
                  Processing Fee
                </span>
                <span className="text-[13px] text-gray-900">
                  {formatWithDecimals(cartResponse?.processingFee ?? 0)}
                </span>
              </div>
            )}
          </div>
          <hr className="border-gray-200 my-3" />
          <div className="flex justify-between items-center">
            <span className="text-[14px] font-semibold text-gray-900">
              Total
            </span>
            <span className="text-[20px] font-bold text-gray-900">
              {formatWithDecimals(cartResponse?.totalAmountWithVAT ?? 0)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-100">
          <span className="text-[11px] text-gray-400 font-dmsans">
            Powered By iziFin
          </span>
        </div>
      </div>

      {/* Account details & payment actions */}
      {paymentMode === 0 && (
        <div className="mt-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-3 font-dmsans">
            <p className="font-semibold text-[14px] text-gray-900 mb-3">
              Account Details
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-500">
                  Account Number
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-900">
                    {cartResponse.accountDetails.accountNumber}
                  </span>
                  <button
                    type="button"
                    aria-label="Copy account number"
                    className="
                    bg-white p-0 text-gray-400 hover:text-gray-700 cursor-pointer"
                    onClick={() =>
                      handleCopy(
                        cartResponse.accountDetails.accountNumber,
                        'Account number'
                      )
                    }
                  >
                    <CopyIcon />
                  </button>
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-500">Account Name</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-900">
                    {cartResponse.accountDetails.accountName}
                  </span>
                  <button
                    type="button"
                    aria-label="Copy account name"
                    className="bg-white p-0 text-gray-400 hover:text-gray-700 cursor-pointer"
                    onClick={() =>
                      handleCopy(
                        cartResponse.accountDetails.accountName,
                        'Account name'
                      )
                    }
                  >
                    <CopyIcon />
                  </button>
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-gray-500">Bank Name</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-gray-900">
                    {cartResponse.accountDetails.bankName}
                  </span>
                  <button
                    type="button"
                    aria-label="Copy bank name"
                    className="bg-white p-0 text-gray-400 hover:text-gray-700 cursor-pointer"
                    onClick={() =>
                      handleCopy(
                        cartResponse.accountDetails.bankName,
                        'Bank name'
                      )
                    }
                  >
                    <CopyIcon />
                  </button>
                </span>
              </div>
            </div>
          </div>

          <button
            className="w-full h-[50px] font-dmsans font-bold
            text-[14px] cursor-pointer text-gray-900
            bg-white border border-gray-200 rounded-xl
            hover:bg-gray-50 transition-colors"
            onClick={async () => {
              const orderDetails = await purchaseService.getSingleOrder(
                cartResponse.orderId
              );
              if (
                orderDetails.status === 'Processing' ||
                orderDetails.status === 'AwaitingApproval'
              ) {
                setListeningResponse({
                  orderStatus: 'Success',
                  paymentOption: paymentMode,
                });
                setOrderStatus(orderDetails?.status);
                toast.success('Payment successful');
              } else {
                toast.error('Payment not received');
              }
            }}
          >
            {loading ? '...Loading' : "I've sent the money"}
          </button>
        </div>
      )}

      {paymentMode === 1 && (
        <button
          type="button"
          className="w-full h-[50px] mt-4 rounded-xl
          text-white bg-black cursor-pointer text-center
          font-dmsans font-bold text-[14px]
          hover:bg-gray-900 transition-colors"
          onClick={() => openFrame(bnplUrl)}
        >
          {bnplText}
        </button>
      )}
    </div>
  );
};

export default CartResponseCard;
